import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, ArrowRight } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(formData.email, formData.password);
      toast.success('Velkommen tilbage!');

      const redirectTo = location.state?.redirectTo;
      // Companies without card go to their dashboard (activate-trial banner is shown there).
      // Only users without access (or companies with truly expired subscription) go to pricing.
      const isCompany = user.user_type === 'company';
      const isBrandNewCompany = isCompany && user.subscription_tier === 'none' && !user.stripe_customer_id;
      if (isBrandNewCompany) {
        navigate('/company-dashboard');
      } else if (!user.has_access) {
        navigate('/abonnement');
      } else if (redirectTo) {
        navigate(redirectTo);
      } else {
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login fejlede. Prøv igen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img src="/logo.png" alt="Vibra" className="h-8 w-auto object-contain" />
            <span className="text-xl font-semibold bronze-text">Vibra</span>
          </Link>
          <h1 className="text-3xl font-bold tracking-tight">Log ind</h1>
          <p className="text-muted-foreground mt-2">
            Velkommen tilbage! Log ind på din konto
          </p>
        </div>

        <Card className="border-border/50 shadow-lg" data-testid="login-card">
          <CardHeader>
            <CardTitle>Log ind</CardTitle>
            <CardDescription>
              Indtast dine oplysninger for at fortsætte
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="din@email.dk"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Adgangskode</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="spinner" />
                    Logger ind...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Log ind
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <p className="text-muted-foreground">
                Har du ikke en konto?{' '}
                <Link to="/register" className="text-primary font-medium hover:underline" data-testid="register-link">
                  Opret konto
                </Link>
              </p>
              <p className="text-muted-foreground mt-2">
                Er du håndværker?{' '}
                <Link to="/register-company" className="text-secondary font-medium hover:underline" data-testid="register-company-link">
                  Opret virksomhed
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
